import { useWorkspaces } from "@/api/workspaces";
import { buildRailWorkspaceMoves } from "@/dnd";
import { classifyTransfer } from "@/dnd/classifyTransfer";
import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DroppableData,
} from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useRailOsDropHighlightId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailFilesDropHighlight";
import { RailOsFileDropZone } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailOsFileDropZone";
import type { FilesRailOsDropHandler } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import { cn } from "@/utils";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { useCallback, useMemo } from "react";

/**
 * Combined OS-file + in-app drop target for a folder in the workspace tree.
 * Workspace-scoped file payloads move (including cross-workspace); canvas /
 * flow-session file payloads copy into the destination folder; a canvas card
 * is re-filed here, since canvases live in this tree too. Chats are not in
 * this tree and are rejected.
 */
export function FilesRailDropTarget({
  workspaceId,
  parentPath,
  onOsDrop,
  children,
  className,
  "data-testid": dataTestId,
}: {
  workspaceId: string;
  /** Destination folder (`""` = workspace root). */
  parentPath: string;
  onOsDrop?: FilesRailOsDropHandler | null;
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  const dropId = droppableId.railWorkspaceFolder(workspaceId, parentPath);
  const osActiveDropId = useRailOsDropHighlightId();
  const { active, over } = useDndContext();

  // A workspace the user can only view accepts nothing — the server would
  // 403 the drop, so the target never lights up (capabilities ride on the
  // workspace response; absence = allowed).
  const { data: allWorkspaces } = useWorkspaces();
  const canEditWorkspace =
    allWorkspaces?.find((ws) => ws.id === workspaceId)?.can_edit !== false;

  const isActiveDropTarget = active
    ? over?.id === dropId
    : osActiveDropId != null && osActiveDropId === String(dropId);

  const droppableData = useMemo((): DroppableData => {
    return {
      surface: "rail-workspace-folder",
      destination: destinationFor("workspace-folder", {
        workspaceId,
        parentPath,
      }),
    };
  }, [workspaceId, parentPath]);

  const droppable = useDroppable({
    id: dropId,
    data: droppableData,
    disabled: !canEditWorkspace,
  });

  const draggable = active ? asDraggableData(active.data.current) : null;

  // Shared-with-me files copy into the workspace (any class), so they bypass the
  // owned-file move-batch validation below and are always a valid drop target.
  const allShared =
    draggable?.kind === "files" &&
    draggable.payloads.length > 0 &&
    draggable.payloads.every((p) => p.sharedSource);

  const allWorkspacePayloads =
    draggable?.kind === "files" &&
    draggable.payloads.every((p) => p.sourceClass === "workspace");

  const crossClassCopyPayloads =
    draggable?.kind === "files" &&
    draggable.payloads.length > 0 &&
    draggable.payloads.every(
      (p) => p.sourceClass === "canvas" || p.sourceClass === "flow-session",
    );

  const railMoveBatch =
    isActiveDropTarget && allWorkspacePayloads && !allShared
      ? buildRailWorkspaceMoves(draggable.payloads, {
          workspaceId,
          parentPath,
        })
      : null;

  const dropInvalid =
    isActiveDropTarget &&
    draggable?.kind === "files" &&
    !crossClassCopyPayloads &&
    !allShared &&
    (railMoveBatch?.rejections.some((r) => r.kind === "self_folder") ||
      (railMoveBatch?.moves.length === 0 &&
        (railMoveBatch?.rejections.length ?? 0) > 0));

  // Section drags (from the active-context rail) copy into a folder named
  // after the section — they should light the same drop highlight as files.
  // `section_to_folder` is the only valid section classification here; an
  // empty section classifies as `noop` and stays unhighlighted.
  const sectionDropValid =
    draggable?.kind === "section" &&
    classifyTransfer(draggable, droppableData.destination).kind ===
      "section_to_folder";

  const showOsHighlight =
    isActiveDropTarget && !active && !!onOsDrop && canEditWorkspace;

  // Chats are organized by chat folders, not by this tree — show the grayed
  // "can't" ring (the classifier returns invalid, so a drop is a no-op).
  const nonFileInvalid =
    isActiveDropTarget && draggable?.kind === "chat-session";

  // A canvas dropped here is re-filed into this folder; light the same
  // highlight files get. A drop back into its own folder classifies as a
  // no-op and stays unlit.
  const canvasDropValid =
    draggable?.kind === "canvas-card" &&
    classifyTransfer(draggable, droppableData.destination).kind ===
      "move_canvas";

  const showDndHighlight =
    isActiveDropTarget &&
    !!active &&
    ((draggable?.kind === "files" && !dropInvalid) ||
      sectionDropValid ||
      canvasDropValid);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      droppable.setNodeRef(el);
    },
    [droppable],
  );

  const showRustHighlight = showOsHighlight || showDndHighlight;

  return (
    <RailOsFileDropZone
      ref={setRef}
      dropId={dropId}
      onDrop={canEditWorkspace ? (onOsDrop ?? null) : null}
      data-testid={dataTestId}
      className={cn(
        cnTransferDropTargetSurface({
          isOver: showRustHighlight || dropInvalid || nonFileInvalid,
          disallowed: dropInvalid || nonFileInvalid,
        }),
        className,
      )}
    >
      {children}
    </RailOsFileDropZone>
  );
}
